import type { GlobalSearch } from './solr-search'
import { transformShortTitle } from '~/utils/solr/law-utils'

interface ResolvedSolrDocumentTitle {
  title?: string | null
  subTitle?: string | null
  journalIssueTitle?: string | null
}

const MAX_JOURNAL_SECTION_COMPOSITE_TITLE_LENGTH = 160

export default function resolveSolrDocumentTitle(doc: GlobalSearch): ResolvedSolrDocumentTitle {
  const { title } = transformShortTitle(doc.shortTitle)
  const childContextTitle = doc.chapterTitle || doc.paragraphTitle
  const parentTitle = title || (doc.title !== childContextTitle ? doc.title : undefined)

  switch (doc.nodeType) {
    case 'law': {
      return resolveLawTitle(doc, childContextTitle, parentTitle)
    }

    case 'legal_commentary': {
      return resolveLegalCommentaryTitle(doc, childContextTitle, parentTitle)
    }

    case 'book':
    case 'journal':
    case 'academic_article':
      return {
        title: doc.title,
        subTitle: childContextTitle || doc.subTitle,
      }

    case 'journal_article':
    case 'journal_editorial':
      return resolveJournalContentTitle(doc)

    case 'author':
      return {
        title: doc.title,
      }

    default:
      return resolveFallbackTitle(doc)
  }
}

function resolveJournalContentTitle(doc: GlobalSearch): ResolvedSolrDocumentTitle {
  const journalSectionTitle = resolveJournalSectionTitle(doc.subTitle, doc.journalSectionTitle)
  if (journalSectionTitle) {
    return {
      title: journalSectionTitle,
      subTitle: doc.subTitle,
      journalIssueTitle: doc.title ?? null,
    }
  }

  if (!hasMeaningfulText(doc.subTitle) && hasMeaningfulText(doc.title)) {
    return {
      title: doc.title,
      subTitle: undefined,
    }
  }

  return {
    title: doc.subTitle,
    subTitle: doc.title,
  }
}

function resolveJournalSectionTitle(parentTitle: unknown, sectionTitle: unknown): string | undefined {
  if (!hasMeaningfulText(parentTitle) || !hasMeaningfulText(sectionTitle)) {
    return undefined
  }
  if (normalizeSolrTitleForComparison(sectionTitle) === normalizeSolrTitleForComparison(parentTitle)) {
    return undefined
  }

  const compositeTitle = `${parentTitle.trim()} / ${sectionTitle.trim()}`
  return [...compositeTitle].length <= MAX_JOURNAL_SECTION_COMPOSITE_TITLE_LENGTH
    ? compositeTitle
    : sectionTitle.trim()
}

function hasMeaningfulText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

export function normalizeSolrTitleForComparison(value: unknown): string {
  return typeof value === 'string'
    ? value.normalize('NFKC').toLowerCase().trim().replace(/\s+/g, ' ')
    : ''
}

function resolveLawTitle(
  doc: GlobalSearch,
  childContextTitle: string | null | undefined,
  parentTitle: string | null | undefined,
): ResolvedSolrDocumentTitle {
  const resolvedTitle = childContextTitle && parentTitle
    ? `${parentTitle} / ${childContextTitle}`
    : childContextTitle || parentTitle || doc.title

  return {
    title: resolvedTitle,
    subTitle: doc.title && doc.title !== resolvedTitle ? doc.title : undefined,
  }
}

function resolveLegalCommentaryTitle(
  doc: GlobalSearch,
  childContextTitle: string | null | undefined,
  parentTitle: string | null | undefined,
): ResolvedSolrDocumentTitle {
  const resolvedTitle = childContextTitle && parentTitle
    ? `${parentTitle} / ${childContextTitle}`
    : parentTitle || childContextTitle || doc.title

  return {
    title: resolvedTitle,
    subTitle: doc.title && doc.title !== resolvedTitle ? doc.title : undefined,
  }
}

function resolveFallbackTitle(doc: GlobalSearch): ResolvedSolrDocumentTitle {
  return {
    title: doc.title || 'Uten tittel',
    subTitle: doc.subTitle || 'Uten undertittel',
  }
}
